class GalleryModal extends HTMLElement {
  connectedCallback() {
    // Move to body so position:fixed is never trapped inside a transformed ancestor
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this._slides = Array.from(this.querySelectorAll('.gallery-modal__slide'));
    this._counter = this.querySelector('.gallery-modal__counter');
    this._current = 0;

    // Transform state
    this._scale = 1;
    this._tx = 0;
    this._ty = 0;
    this._panning = false;
    this._didDrag = false;
    this._panOx = 0;
    this._panOy = 0;

    // Touch state
    this._touchX = 0;
    this._touchY = 0;
    this._touchTime = 0;
    this._lastTap = 0;
    this._pinchDist = 0;
    this._pinchScale = 1;

    this.querySelector('.gallery-modal__close').addEventListener('click', () => this.close());
    this.querySelector('.gallery-modal__prev')?.addEventListener('click', () => this.navigate(-1));
    this.querySelector('.gallery-modal__next')?.addEventListener('click', () => this.navigate(1));
    this.querySelector('.gallery-modal__zoom')?.addEventListener('click', () => {
      if (this._scale > 1) {
        this._resetTransform();
      } else {
        this._zoomAt(window.innerWidth / 2, window.innerHeight / 2, 2.5);
      }
    });

    document.addEventListener('keydown', (e) => this._onKey(e));

    const track = this.querySelector('.gallery-modal__track');

    // Close when clicking outside the image (backdrop / slide padding area)
    track.addEventListener('click', (e) => {
      if (this._didDrag) { this._didDrag = false; return; }
      if (!e.target.closest('img')) this.close();
    });

    track.addEventListener('touchstart', (e) => this._touchStart(e), { passive: false });
    track.addEventListener('touchmove', (e) => this._touchMove(e), { passive: false });
    track.addEventListener('touchend', (e) => this._touchEnd(e));
    track.addEventListener('dblclick', (e) => this._dblClick(e));
    track.addEventListener('mousedown', (e) => this._mouseDown(e));
    document.addEventListener('mousemove', (e) => this._mouseMove(e));
    document.addEventListener('mouseup', () => this._mouseUp());

    this._initTrigger();
  }

  _initTrigger() {
    const gallery = document.getElementById('MediaGallery-' + this.dataset.section);
    if (!gallery) return;
    gallery.addEventListener('click', (e) => {
      if (e.target.closest('.main-product__media--thumbs-wrapper')) return;
      const slide = e.target.closest('[data-media-type="image"]');
      if (!slide) return;
      e.preventDefault();
      const idx = this._slides.findIndex((s) => s.dataset.mediaId === slide.dataset.mediaId);
      if (idx >= 0) this.open(idx);
    });
  }

  open(index) {
    this._current = Math.max(0, Math.min(index, this._slides.length - 1));
    this.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this._resetTransform();
    this._showSlide();
    this._loadImages(this._current);
    this.querySelector('.gallery-modal__close').focus();
  }

  close() {
    this.classList.remove('is-open');
    document.body.style.overflow = '';
    this._resetTransform();
  }

  navigate(dir) {
    this._resetTransform();
    this._current = (this._current + dir + this._slides.length) % this._slides.length;
    this._showSlide();
    this._loadImages(this._current);
  }

  _showSlide() {
    this._slides.forEach((s, i) => s.classList.toggle('is-active', i === this._current));
    if (this._counter) this._counter.textContent = (this._current + 1) + ' / ' + this._slides.length;
  }

  _loadImages(center) {
    [-1, 0, 1].forEach((offset) => {
      const i = (center + offset + this._slides.length) % this._slides.length;
      const img = this._slides[i]?.querySelector('img');
      if (img && !img.src && img.dataset.src) img.src = img.dataset.src;
    });
  }

  _onKey(e) {
    if (!this.classList.contains('is-open')) return;
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowLeft') this.navigate(-1);
    if (e.key === 'ArrowRight') this.navigate(1);
  }

  // ── Touch ───────────────────────────────────────────────────────────────

  _touchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      this._pinchDist = this._pinchDistance(e.touches);
      this._pinchScale = this._scale;
    } else {
      this._touchX = e.touches[0].clientX;
      this._touchY = e.touches[0].clientY;
      this._touchTime = Date.now();
      if (this._scale > 1) {
        e.preventDefault();
        this._panning = true;
        this._panOx = e.touches[0].clientX - this._tx;
        this._panOy = e.touches[0].clientY - this._ty;
      }
    }
  }

  _touchMove(e) {
    if (e.touches.length === 2) {
      e.preventDefault();
      const d = this._pinchDistance(e.touches);
      this._scale = Math.min(4, Math.max(1, this._pinchScale * (d / this._pinchDist)));
      this._applyTransform();
    } else if (this._panning && this._scale > 1) {
      e.preventDefault();
      this._tx = e.touches[0].clientX - this._panOx;
      this._ty = e.touches[0].clientY - this._panOy;
      this._clampPan();
      this._applyTransform();
    }
  }

  _touchEnd(e) {
    if (e.changedTouches.length !== 1) {
      if (this._scale < 1.05) this._resetTransform();
      else this._clampPan();
      return;
    }

    const t = e.changedTouches[0];
    const dx = t.clientX - this._touchX;
    const dy = t.clientY - this._touchY;
    const dt = Date.now() - this._touchTime;

    if (!this._panning) {
      if (Math.abs(dx) < 15 && Math.abs(dy) < 15 && dt < 300) {
        const now = Date.now();
        if (now - this._lastTap < 350) {
          // Double tap
          this._scale > 1 ? this._resetTransform() : this._zoomAt(t.clientX, t.clientY, 2.5);
          this._lastTap = 0;
        } else {
          this._lastTap = now;
          // Single tap outside image → close
          const img = this._currentImg();
          if (img) {
            const r = img.getBoundingClientRect();
            const onImg = t.clientX >= r.left && t.clientX <= r.right && t.clientY >= r.top && t.clientY <= r.bottom;
            if (!onImg) this.close();
          }
        }
      } else if (this._scale <= 1 && Math.abs(dx) > 50 && Math.abs(dy) < 60) {
        this.navigate(dx < 0 ? 1 : -1);
      }
    }

    this._panning = false;
    if (this._scale < 1.05) this._resetTransform();
  }

  // ── Mouse pan ───────────────────────────────────────────────────────────

  _dblClick(e) {
    this._scale > 1 ? this._resetTransform() : this._zoomAt(e.clientX, e.clientY, 2.5);
  }

  _mouseDown(e) {
    if (this._scale <= 1 || e.button !== 0) return;
    this._didDrag = false;
    this._panning = true;
    this._panOx = e.clientX - this._tx;
    this._panOy = e.clientY - this._ty;
    this._currentImg()?.classList.add('is-grabbing');
    e.preventDefault();
  }

  _mouseMove(e) {
    if (!this._panning) return;
    this._didDrag = true;
    this._tx = e.clientX - this._panOx;
    this._ty = e.clientY - this._panOy;
    this._clampPan();
    this._applyTransform();
  }

  _mouseUp() {
    if (!this._panning) return;
    this._panning = false;
    this._currentImg()?.classList.remove('is-grabbing');
  }

  // ── Zoom ────────────────────────────────────────────────────────────────

  _zoomAt(cx, cy, scale) {
    const img = this._currentImg();
    if (!img) return;
    const r = img.getBoundingClientRect();
    const ox = cx - (r.left + r.width / 2);
    const oy = cy - (r.top + r.height / 2);
    this._scale = scale;
    this._tx = ox * (1 - scale);
    this._ty = oy * (1 - scale);
    this._applyTransform();
  }

  // _tx/_ty are visual pixel offsets; keep the image within the viewport while panning
  _clampPan() {
    const img = this._currentImg();
    if (!img || this._scale <= 1) return;
    const r = img.getBoundingClientRect();
    // Divide current rendered size by scale to get the base (scale=1) rendered size
    const baseW = r.width / this._scale;
    const baseH = r.height / this._scale;
    const maxTx = Math.max(0, (baseW * this._scale - window.innerWidth) / 2);
    const maxTy = Math.max(0, (baseH * this._scale - window.innerHeight) / 2);
    this._tx = Math.max(-maxTx, Math.min(maxTx, this._tx));
    this._ty = Math.max(-maxTy, Math.min(maxTy, this._ty));
  }

  _applyTransform() {
    const img = this._currentImg();
    if (!img) return;
    const zoomed = this._scale > 1;
    if (!zoomed) {
      img.style.transform = '';
      img.style.cursor = '';
    } else {
      img.style.transform = `scale(${this._scale}) translate(${this._tx / this._scale}px, ${this._ty / this._scale}px)`;
      img.style.cursor = this._panning ? 'grabbing' : 'grab';
    }
    img.classList.toggle('is-zoomed', zoomed);

    const zoomBtn = this.querySelector('.gallery-modal__zoom');
    if (zoomBtn) {
      zoomBtn.classList.toggle('is-zoomed', zoomed);
      zoomBtn.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
    }
  }

  _resetTransform() {
    this._scale = 1;
    this._tx = 0;
    this._ty = 0;
    this._panning = false;
    this._didDrag = false;
    this._applyTransform();
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  _currentImg() {
    return this._slides[this._current]?.querySelector('img');
  }

  _pinchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}

customElements.define('gallery-modal', GalleryModal);
